import React, { useState, useRef } from 'react';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL, POST_GROUP_MASTER_BULK } from '../../utils/config';
import { getWebUserInfo } from '../../utils/webConfig';

// Full screen loader with progress
const ProgressLoader = ({ progress, current, total, status }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
          <FiUpload className="text-indigo-600" size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Uploading Groups</h3>
        <p className="text-gray-600 text-sm">{status}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress: {current} / {total}</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="bg-gradient-to-r from-indigo-600 to-purple-600 h-4 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        Please wait while groups are being uploaded...
      </div>
    </div>
  </div>
);

const AddGroupMaster = () => {
  const [groupMasterFile, setGroupMasterFile] = useState(null);
  const [groupChildFile, setGroupChildFile] = useState(null);
  const [groupMasterData, setGroupMasterData] = useState([]);
  const [groupChildData, setGroupChildData] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [status, setStatus] = useState('');
  const [uploadResults, setUploadResults] = useState({ success: 0, failed: 0, errors: [] });
  const groupMasterFileInputRef = useRef(null);
  const groupChildFileInputRef = useRef(null);

  const token = localStorage.getItem('accessToken');
  
  // Get tenant name from clientInfo or localStorage
  const getTenantName = () => {
    try {
      const clientInfoStr = localStorage.getItem('clientInfo');
      if (clientInfoStr) {
        const clientInfo = JSON.parse(clientInfoStr);
        if (clientInfo.TenantName) {
          return clientInfo.TenantName;
        }
      }
    } catch (e) {
      console.error('Error parsing clientInfo:', e);
    }
    
    // Fallback to webUserInfo
    const webUserInfo = getWebUserInfo();
    if (webUserInfo?.clientInfo?.TenantName) {
      return webUserInfo.clientInfo.TenantName;
    }
    
    // Final fallback
    return localStorage.getItem('firm_name') || '';
  };

  // Helper function to convert Excel value to proper type
  const convertValue = (value) => {
    if (value === null || value === undefined || value === 'NULL' || value === '') {
      return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
    return value;
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleGroupMasterFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setGroupMasterFile(selectedFile);
        setParsing(true);
        
        try {
          const data = await parseExcelFile(selectedFile);
          if (data && data.length > 0) {
            setGroupMasterData(data);
            toast.success(`Successfully read ${data.length} groups from Group Master file`, { autoClose: 2000 });
          } else {
            toast.error('Group Master Excel file is empty or invalid', { autoClose: 2000 });
            setGroupMasterFile(null);
            setGroupMasterData([]);
          }
        } catch (error) {
          console.error('Error parsing Group Master file:', error);
          toast.error('Failed to parse Group Master Excel file: ' + (error.message || 'Unknown error'), { autoClose: 2000 });
          setGroupMasterFile(null);
          setGroupMasterData([]);
        } finally {
          setParsing(false);
        }
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)', { autoClose: 2000 });
        setGroupMasterFile(null);
        setGroupMasterData([]);
      }
    }
  };

  const handleGroupChildFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setGroupChildFile(selectedFile);
        setParsing(true);
        
        try {
          const data = await parseExcelFile(selectedFile);
          if (data && data.length > 0) {
            setGroupChildData(data);
            toast.success(`Successfully read ${data.length} rows from Group Child file`, { autoClose: 2000 });
          } else {
            toast.error('Group Child Excel file is empty or invalid', { autoClose: 2000 });
            setGroupChildFile(null);
            setGroupChildData([]);
          }
        } catch (error) {
          console.error('Error parsing Group Child file:', error);
          toast.error('Failed to parse Group Child Excel file: ' + (error.message || 'Unknown error'), { autoClose: 2000 });
          setGroupChildFile(null);
          setGroupChildData([]);
        } finally {
          setParsing(false);
        }
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)', { autoClose: 2000 });
        setGroupChildFile(null);
        setGroupChildData([]);
      }
    }
  };

  // Process Group Child data to create testIDs array for each GroupID
  const processGroupChildData = () => {
    const groupTestMap = {};
    
    // Debug: Log first row structure to understand column names
    if (groupChildData.length > 0) {
      console.log('First child row structure:', groupChildData[0]);
      console.log('All column names:', Object.keys(groupChildData[0]));
    }
    
    // Process all rows and build testIDs array for each GroupID
    groupChildData.forEach((row, index) => {
      const groupID = row.GroupID || row['GroupID']; // Column A - GroupID
      const type = row.Type || row['Type'];
      const testID = row.TestID || row['TestID']; // Column D - TestID (for Type='T')
      
      // Column C might be named "GroupID" (same as Column A) or "GrpID" - need to handle both
      // When Excel has duplicate column names, XLSX might name the second one differently
      // Try various possible names, and also try to get by position (3rd column)
      let grpID = row.GrpID || row['GrpID'] || 
                  row['GroupID_1'] || row['GroupID.1'] || 
                  row['GroupID (C)'] || row['GroupID_C'];
      
      // If not found by name, try to get by position (Column C is 3rd column)
      // Get all keys and values, find the 3rd one (index 2) that's not already used
      if (!grpID || grpID === 'NULL' || grpID === null) {
        const keys = Object.keys(row);
        const values = Object.values(row);
        // Column order: A=GroupID, B=SI_No, C=GroupID/GrpID, D=TestID, E=Type, F=SortOrder
        // Try to find Column C value - it should be a numeric value when Type='G'
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          const val = values[i];
          // Skip known columns, look for a numeric value that could be Column C
          if (key !== 'GroupID' && key !== 'SI_No' && key !== 'SI No' && 
              key !== 'Type' && key !== 'TestID' && key !== 'SortOrder' && key !== 'Sort_Order' &&
              val !== null && val !== 'NULL' && val !== '' && !isNaN(parseInt(val))) {
            // This might be Column C
            grpID = val;
            break;
          }
        }
      }
      
      const sortOrder = row.SortOrder || row['SortOrder'] || row['Sort_Order']; // Column F - SortOrder
      
      if (!groupID) return;
      
      // Convert groupID to string for consistent matching
      const groupIDKey = String(groupID);
      
      if (!groupTestMap[groupIDKey]) {
        groupTestMap[groupIDKey] = [];
      }
      
      // Handle Type='G' rows
      // For Type='G': testId = 0, grpID = value from Column C (GroupID column which contains GrpID value)
      if (type === 'G') {
        let grpIDValue = 0;
        if (grpID && grpID !== 'NULL' && grpID !== null && grpID !== '') {
          const parsedGrpID = parseInt(grpID);
          if (!isNaN(parsedGrpID)) {
            grpIDValue = parsedGrpID;
          }
        }
        
        // Debug log for GroupID 2
        if (groupID === 2 || groupID === '2') {
          console.log(`Type='G' row for GroupID 2:`, {
            groupID,
            type,
            grpID,
            grpIDValue,
            sortOrder,
            fullRow: row
          });
        }
        
        groupTestMap[groupIDKey].push({
          testId: null, // testId is 0 for Type='G'
          grpID: grpIDValue, // grpID from Column C
          type: 'G',
          sortOrder: sortOrder ? parseInt(sortOrder) : 0
        });
      }
      
      // Handle Type='T' rows
      // For Type='T': testId = TestID from Column D, grpID = 0
      if (type === 'T' && testID && testID !== 'NULL' && testID !== null && testID !== '') {
        const parsedTestID = parseInt(testID);
        if (!isNaN(parsedTestID)) {
          groupTestMap[groupIDKey].push({
            testId: parsedTestID, // testId from Column D (TestID)
            grpID: null, // grpID is 0 for Type='T'
            type: 'T',
            sortOrder: sortOrder ? parseInt(sortOrder) : 0
          });
        }
      }
    });
    
    return groupTestMap;
  };

  const handleUpload = async () => {
    if (!groupMasterFile || groupMasterData.length === 0) {
      toast.error('Please upload Group Master Excel file first', { autoClose: 2000 });
      return;
    }

    if (!groupChildFile || groupChildData.length === 0) {
      toast.error('Please upload Group Child Excel file first', { autoClose: 2000 });
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    setUploading(true);
    setProgress(0);
    setCurrentIndex(0);
    setUploadResults({ success: 0, failed: 0, errors: [] });

    try {
      // Process Group Child data to get testIDs for each group
      const groupTestMap = processGroupChildData();
      
      setTotalGroups(groupMasterData.length);
      setStatus(`Preparing to upload ${groupMasterData.length} groups individually...`);

      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      // Upload each group individually with 1-second delay between calls
      for (let i = 0; i < groupMasterData.length; i++) {
        const row = groupMasterData[i];
        const groupID = row.GroupID || row['GroupID'];
        const groupName = convertValue(row.GroupName || row['GroupName']) || `Group ${i + 1}`;
        
        // Parse group master fields
        const groupIDValue = row.GroupID || row['GroupID'];
        let parsedGroupID = 0;
        if (groupIDValue !== null && groupIDValue !== undefined && groupIDValue !== 'NULL' && groupIDValue !== '') {
          const parsed = parseInt(groupIDValue);
          if (!isNaN(parsed)) {
            parsedGroupID = parsed;
          }
        }
        
        // Parse discount
        let discount = 0;
        const discountValue = row.Discount || row['Discount'];
        if (discountValue !== null && discountValue !== undefined && discountValue !== 'NULL' && discountValue !== '') {
          const parsedDiscount = parseFloat(discountValue);
          if (!isNaN(parsedDiscount)) {
            discount = parsedDiscount;
          }
        }
        
        // Get testIDs for this group from child data
        const groupIDKey = String(groupID);
        const testIDs = groupTestMap[groupIDKey] || [];
        
        const groupData = {
          groupID: parsedGroupID,
          groupName: convertValue(row.GroupName || row['GroupName']) || '',
          section: convertValue(row.Section || row['Section']) || '',
          groupCode: convertValue(row.GroupCode || row['GroupCode']) || '',
          rate: parseFloat(row.Rate || row['Rate'] || 0) || 0,
          machineName: '',
          showInReport: true,
          discount: 0,
          altTestName: '',
          avgDays: '',
          avgHour: '',
          avgMinute: '',
          tubeType: '',
          testIDs: testIDs
        };

        // Update progress
        setCurrentIndex(i + 1);
        const progressPercent = Math.round(((i + 1) / groupMasterData.length) * 100);
        setProgress(progressPercent);
        setStatus(`Uploading group ${i + 1} of ${groupMasterData.length}: ${groupName}...`);

        try {
          // Send single group as an array (API expects array format)
          const response = await axios.post(`${BASE_URL}${POST_GROUP_MASTER_BULK}`, [groupData], {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY || '',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.status === 200 || response.status === 201) {
            successCount++;
            setUploadResults({ success: successCount, failed: failedCount, errors });
          } else {
            failedCount++;
            errors.push({
              index: i + 1,
              groupName: groupName,
              error: `API returned status ${response.status}`
            });
            setUploadResults({ success: successCount, failed: failedCount, errors });
          }
        } catch (error) {
          console.error(`Upload error for group ${i + 1}:`, error);
          failedCount++;
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          errors.push({
            index: i + 1,
            groupName: groupName,
            error: errorMessage
          });
          setUploadResults({ success: successCount, failed: failedCount, errors });
        }

        // Add 1-second delay between API calls (except after the last one)
        if (i < groupMasterData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setProgress(100);
      setCurrentIndex(groupMasterData.length);
      setStatus('Upload completed!');

      if (failedCount === 0) {
        toast.success(`Successfully uploaded all ${successCount} groups!`, { autoClose: 3000 });
      } else if (successCount > 0) {
        toast.warning(`Upload completed: ${successCount} succeeded, ${failedCount} failed`, { autoClose: 3000 });
      } else {
        toast.error(`All ${failedCount} groups failed to upload`, { autoClose: 3000 });
      }

      // Wait a bit before allowing user to continue
      await new Promise(resolve => setTimeout(resolve, 2000));
      setUploading(false);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const results = {
        success: 0,
        failed: groupMasterData.length,
        errors: [{
          index: 0,
          groupName: 'Upload Process',
          error: errorMessage
        }]
      };
      setUploadResults(results);
      setStatus('Upload failed!');
      toast.error('Failed to upload groups: ' + errorMessage, { autoClose: 3000 });
      setUploading(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {uploading && (
        <ProgressLoader
          progress={progress}
          current={currentIndex}
          total={totalGroups}
          status={status}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FiFile size={32} className="text-blue-600" />
          Add Group Master
        </h1>
        <p className="text-gray-500 mt-2">Upload Excel files to bulk add group master data</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="space-y-6">
          {/* Group Master File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Master Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={groupMasterFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleGroupMasterFileChange}
                disabled={uploading}
                className="hidden"
                id="group-master-upload"
              />
              <label
                htmlFor="group-master-upload"
                className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FiUpload size={48} className="text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {groupMasterFile ? groupMasterFile.name : 'Click to upload Group Master Excel file'}
                </p>
                <p className="text-sm text-gray-400">
                  Excel files only (.xlsx, .xls)
                </p>
              </label>
            </div>
            {groupMasterFile && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FiCheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">{groupMasterFile.name}</span>
                {parsing && (
                  <span className="text-blue-600 text-sm ml-2">(Parsing...)</span>
                )}
                {!parsing && groupMasterData.length > 0 && (
                  <span className="text-green-600 text-sm ml-2">({groupMasterData.length} groups found)</span>
                )}
                <button
                  onClick={() => {
                    setGroupMasterFile(null);
                    setGroupMasterData([]);
                    if (groupMasterFileInputRef.current) {
                      groupMasterFileInputRef.current.value = '';
                    }
                  }}
                  className="ml-auto text-red-600 hover:text-red-700"
                  disabled={uploading || parsing}
                >
                  <FiXCircle size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Group Child File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Child Excel File (TestIDs)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={groupChildFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleGroupChildFileChange}
                disabled={uploading}
                className="hidden"
                id="group-child-upload"
              />
              <label
                htmlFor="group-child-upload"
                className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FiUpload size={48} className="text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {groupChildFile ? groupChildFile.name : 'Click to upload Group Child Excel file'}
                </p>
                <p className="text-sm text-gray-400">
                  Excel files only (.xlsx, .xls)
                </p>
              </label>
            </div>
            {groupChildFile && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FiCheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">{groupChildFile.name}</span>
                {parsing && (
                  <span className="text-blue-600 text-sm ml-2">(Parsing...)</span>
                )}
                {!parsing && groupChildData.length > 0 && (
                  <span className="text-green-600 text-sm ml-2">({groupChildData.length} rows found)</span>
                )}
                <button
                  onClick={() => {
                    setGroupChildFile(null);
                    setGroupChildData([]);
                    if (groupChildFileInputRef.current) {
                      groupChildFileInputRef.current.value = '';
                    }
                  }}
                  className="ml-auto text-red-600 hover:text-red-700"
                  disabled={uploading || parsing}
                >
                  <FiXCircle size={20} />
                </button>
              </div>
            )}
          </div>



          {/* Upload Button */}
          {groupMasterData.length > 0 && groupChildData.length > 0 && (
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setGroupMasterFile(null);
                  setGroupChildFile(null);
                  setGroupMasterData([]);
                  setGroupChildData([]);
                  setUploadResults({ success: 0, failed: 0, errors: [] });
                  if (groupMasterFileInputRef.current) {
                    groupMasterFileInputRef.current.value = '';
                  }
                  if (groupChildFileInputRef.current) {
                    groupChildFileInputRef.current.value = '';
                  }
                }}
                disabled={uploading || parsing}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Clear
              </button>
              <button
                onClick={handleUpload}
                disabled={!groupMasterFile || !groupChildFile || uploading || parsing || groupMasterData.length === 0 || groupChildData.length === 0}
                className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg ${
                  (!groupMasterFile || !groupChildFile || uploading || parsing || groupMasterData.length === 0 || groupChildData.length === 0) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Uploading...' : `Submit ${groupMasterData.length} Groups`}
              </button>
            </div>
          )}

          {/* Upload Results */}
          {!uploading && uploadResults.success + uploadResults.failed > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Upload Results</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{uploadResults.success}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{uploadResults.failed}</p>
                </div>
              </div>
              {uploadResults.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {uploadResults.errors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 p-2 bg-red-50 rounded">
                        {error.groupName || `Group ${error.index}`}: {error.error}
                      </div>
                    ))}
                    {uploadResults.errors.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        ... and {uploadResults.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddGroupMaster;
